import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { AreaChart, Area, XAxis, YAxis, ResponsiveContainer, Tooltip } from "recharts";

const CALCS = [
  { id: "sip", title: "SIP Calculator", tag: "Wealth building", desc: "Project the corpus your monthly SIP could build." },
  { id: "emi", title: "EMI Calculator", tag: "Loans", desc: "Understand the true cost of borrowing." },
  { id: "retirement", title: "Retirement Planner", tag: "Long-term", desc: "How much do you need to retire comfortably?" },
  { id: "goal", title: "Goal Planner", tag: "Goal-based", desc: "Save for a target — home, education, wedding." },
  { id: "tax", title: "Tax Estimator", tag: "New regime", desc: "Rough income tax under FY 2025-26 new regime." },
];

export function CalculatorsIndex() {
  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
      <div className="overline text-gold-dark mb-6">Tools</div>
      <h1 className="font-display text-5xl md:text-6xl text-midnight tracking-tight mb-6">Financial <span className="italic">calculators</span>.</h1>
      <p className="text-midnight/70 max-w-2xl mb-14">Play with the sliders. Ballpark answers before you talk to us.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {CALCS.map((c) => (
          <Link key={c.id} to={`/calculators/${c.id}`} data-testid={`calc-card-${c.id}`} className="p-8 bg-white rounded-2xl border border-black/5 hover:border-gold/40 transition-colors">
            <div className="overline text-gold-dark mb-3">{c.tag}</div>
            <h3 className="font-display text-2xl text-midnight mb-3">{c.title}</h3>
            <p className="text-sm text-midnight/70">{c.desc}</p>
          </Link>
        ))}
      </div>
    </div>
  );
}

const inr = (n) => "₹" + Math.round(n).toLocaleString("en-IN");

function Slider({ label, value, min, max, step, onChange, format, testid }) {
  return (
    <div>
      <div className="flex justify-between items-baseline">
        <label className="overline text-midnight/70">{label}</label>
        <div className="font-display text-lg text-midnight">{format ? format(value) : value}</div>
      </div>
      <input type="range" data-testid={testid} min={min} max={max} step={step} value={value} onChange={(e) => onChange(Number(e.target.value))} className="mt-3 w-full accent-[#C5A059] h-2 bg-midnight/10 rounded-full appearance-none cursor-pointer" />
    </div>
  );
}

function Result({ label, value, big }) {
  return (
    <div>
      <div className="overline text-midnight/60 mb-2">{label}</div>
      <div className={`font-display text-midnight ${big ? "text-5xl" : "text-2xl"}`}>{value}</div>
    </div>
  );
}

function LeadCTA({ source, meta }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/leads", { name, email, source, metadata: meta, message: "Sent from calculator" });
      toast.success("We'll send you a personalised projection shortly.");
      setName(""); setEmail("");
    } catch (err) { toast.error(formatErr(err)); } finally { setLoading(false); }
  };
  return (
    <form onSubmit={submit} data-testid={`calc-lead-${source}`} className="mt-10 p-6 bg-midnight rounded-2xl text-white">
      <div className="font-display text-xl mb-4">Get this analysis by email</div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <input required placeholder="Your name" value={name} onChange={(e)=>setName(e.target.value)} data-testid={`calc-lead-name-${source}`} className="px-4 py-3 rounded-full bg-white/10 border border-white/15 text-sm placeholder:text-white/40" />
        <input required type="email" placeholder="your@email.com" value={email} onChange={(e)=>setEmail(e.target.value)} data-testid={`calc-lead-email-${source}`} className="px-4 py-3 rounded-full bg-white/10 border border-white/15 text-sm placeholder:text-white/40" />
        <button data-testid={`calc-lead-submit-${source}`} disabled={loading} className="btn-gold text-sm">{loading ? "Sending…" : "Send me the plan"}</button>
      </div>
    </form>
  );
}

function Shell({ title, tag, children }) {
  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 py-20">
      <Link to="/calculators" className="text-sm text-midnight/60 hover:text-midnight">← All calculators</Link>
      <div className="overline text-gold-dark mt-6 mb-3">{tag}</div>
      <h1 className="font-display text-5xl text-midnight tracking-tight">{title}</h1>
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-8">
        {children}
      </motion.div>
    </div>
  );
}

export function SIPCalculator() {
  const [amount, setAmount] = useState(10000);
  const [years, setYears] = useState(15);
  const [rate, setRate] = useState(12);
  const months = years * 12;
  const r = rate / 100 / 12;
  const fv = amount * (((Math.pow(1 + r, months) - 1) / r) * (1 + r));
  const invested = amount * months;
  const gain = fv - invested;
  const data = Array.from({ length: years + 1 }, (_, y) => {
    const m = y * 12;
    const val = m === 0 ? 0 : amount * (((Math.pow(1 + r, m) - 1) / r) * (1 + r));
    return { year: y, value: Math.round(val), invested: amount * m };
  });
  return (
    <Shell title="SIP Calculator" tag="Wealth building">
      <div className="bg-white rounded-2xl p-8 border border-black/5 space-y-6">
        <Slider label="Monthly SIP" value={amount} min={500} max={200000} step={500} onChange={setAmount} format={inr} testid="sip-amount" />
        <Slider label="Years" value={years} min={1} max={40} step={1} onChange={setYears} testid="sip-years" />
        <Slider label="Expected return (p.a.)" value={rate} min={4} max={20} step={0.5} onChange={setRate} format={(v)=>v+"%"} testid="sip-rate" />
      </div>
      <div className="bg-midnight rounded-2xl p-8 text-white space-y-6">
        <Result label="Projected corpus" value={inr(fv)} big />
        <div className="grid grid-cols-2 gap-4">
          <div><div className="overline text-white/50 mb-1">Invested</div><div className="font-display text-xl">{inr(invested)}</div></div>
          <div><div className="overline text-gold mb-1">Gains</div><div className="font-display text-xl text-gold">{inr(gain)}</div></div>
        </div>
        <div className="h-40">
          <ResponsiveContainer>
            <AreaChart data={data}>
              <defs><linearGradient id="g" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#C5A059" stopOpacity={0.7}/><stop offset="100%" stopColor="#C5A059" stopOpacity={0}/></linearGradient></defs>
              <XAxis dataKey="year" stroke="#ffffff40" fontSize={11} />
              <YAxis stroke="#ffffff40" fontSize={11} tickFormatter={(v)=>`${Math.round(v/100000)}L`} />
              <Tooltip contentStyle={{ background: "#123524", border: "1px solid #C5A059" }} formatter={(v)=>inr(v)} />
              <Area type="monotone" dataKey="value" stroke="#C5A059" fill="url(#g)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="lg:col-span-2"><LeadCTA source="sip" meta={{ amount, years, rate, fv }} /></div>
    </Shell>
  );
}

export function EMICalculator() {
  const [principal, setPrincipal] = useState(3000000);
  const [rate, setRate] = useState(9);
  const [years, setYears] = useState(20);
  const n = years * 12;
  const r = rate / 100 / 12;
  const emi = (principal * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
  const total = emi * n;
  const interest = total - principal;
  return (
    <Shell title="EMI Calculator" tag="Loans">
      <div className="bg-white rounded-2xl p-8 border border-black/5 space-y-6">
        <Slider label="Loan amount" value={principal} min={100000} max={20000000} step={50000} onChange={setPrincipal} format={inr} testid="emi-principal" />
        <Slider label="Interest rate" value={rate} min={5} max={20} step={0.1} onChange={setRate} format={(v)=>v+"%"} testid="emi-rate" />
        <Slider label="Tenure (years)" value={years} min={1} max={30} step={1} onChange={setYears} testid="emi-years" />
      </div>
      <div className="bg-midnight rounded-2xl p-8 text-white space-y-6">
        <Result label="Monthly EMI" value={inr(emi)} big />
        <div className="grid grid-cols-2 gap-4">
          <div><div className="overline text-white/50 mb-1">Interest</div><div className="font-display text-xl text-gold">{inr(interest)}</div></div>
          <div><div className="overline text-white/50 mb-1">Total payable</div><div className="font-display text-xl">{inr(total)}</div></div>
        </div>
      </div>
      <div className="lg:col-span-2"><LeadCTA source="emi" meta={{ principal, rate, years, emi }} /></div>
    </Shell>
  );
}

export function RetirementCalculator() {
  const [age, setAge] = useState(35);
  const [retireAge, setRetireAge] = useState(60);
  const [expense, setExpense] = useState(80000);
  const [infl, setInfl] = useState(6);
  const [rate, setRate] = useState(12);
  const years = retireAge - age;
  const futureExp = expense * Math.pow(1 + infl / 100, years);
  const corpus = futureExp * 12 * 25; // 25x annual
  const months = years * 12;
  const r = rate / 100 / 12;
  const sip = months > 0 ? corpus / ((Math.pow(1 + r, months) - 1) / r * (1 + r)) : 0;
  return (
    <Shell title="Retirement Planner" tag="Long-term">
      <div className="bg-white rounded-2xl p-8 border border-black/5 space-y-6">
        <Slider label="Current age" value={age} min={18} max={55} step={1} onChange={setAge} testid="ret-age" />
        <Slider label="Retirement age" value={retireAge} min={40} max={70} step={1} onChange={setRetireAge} testid="ret-retire-age" />
        <Slider label="Monthly expense today" value={expense} min={20000} max={500000} step={5000} onChange={setExpense} format={inr} testid="ret-expense" />
        <Slider label="Inflation" value={infl} min={2} max={10} step={0.5} onChange={setInfl} format={(v)=>v+"%"} testid="ret-infl" />
        <Slider label="Expected return" value={rate} min={6} max={16} step={0.5} onChange={setRate} format={(v)=>v+"%"} testid="ret-rate" />
      </div>
      <div className="bg-midnight rounded-2xl p-8 text-white space-y-6">
        <Result label="Corpus needed" value={inr(corpus)} big />
        <div className="grid grid-cols-2 gap-4">
          <div><div className="overline text-white/50 mb-1">SIP required</div><div className="font-display text-xl text-gold">{inr(sip)}/mo</div></div>
          <div><div className="overline text-white/50 mb-1">Future expense</div><div className="font-display text-xl">{inr(futureExp)}/mo</div></div>
        </div>
      </div>
      <div className="lg:col-span-2"><LeadCTA source="retirement" meta={{ age, retireAge, expense, corpus, sip }} /></div>
    </Shell>
  );
}

export function GoalCalculator() {
  const [target, setTarget] = useState(5000000);
  const [years, setYears] = useState(10);
  const [rate, setRate] = useState(12);
  const months = years * 12;
  const r = rate / 100 / 12;
  const sip = target / (((Math.pow(1 + r, months) - 1) / r) * (1 + r));
  return (
    <Shell title="Goal Planner" tag="Goal-based">
      <div className="bg-white rounded-2xl p-8 border border-black/5 space-y-6">
        <Slider label="Goal amount" value={target} min={100000} max={100000000} step={100000} onChange={setTarget} format={inr} testid="goal-target" />
        <Slider label="Years to goal" value={years} min={1} max={35} step={1} onChange={setYears} testid="goal-years" />
        <Slider label="Expected return" value={rate} min={4} max={20} step={0.5} onChange={setRate} format={(v)=>v+"%"} testid="goal-rate" />
      </div>
      <div className="bg-midnight rounded-2xl p-8 text-white space-y-6">
        <Result label="Monthly SIP needed" value={inr(sip)} big />
        <p className="text-sm text-white/60">Assuming monthly compounding and consistent contributions.</p>
      </div>
      <div className="lg:col-span-2"><LeadCTA source="goal" meta={{ target, years, rate, sip }} /></div>
    </Shell>
  );
}

export function TaxCalculator() {
  const [income, setIncome] = useState(1500000);
  // FY 2025-26 new regime (illustrative)
  const slabs = [[400000,0],[400000,0.05],[400000,0.1],[400000,0.15],[400000,0.2],[Infinity,0.3]];
  let taxable = Math.max(0, income - 75000); // std deduction
  let tax = 0; let remaining = taxable;
  for (const [width, rate] of slabs) { const t = Math.min(remaining, width); tax += t * rate; remaining -= t; if (remaining<=0) break; }
  // rebate 87A up to 12L taxable
  if (taxable <= 1200000) tax = 0;
  tax = tax * 1.04; // cess
  return (
    <Shell title="Tax Estimator" tag="New regime FY 2025-26">
      <div className="bg-white rounded-2xl p-8 border border-black/5 space-y-6">
        <Slider label="Annual income" value={income} min={300000} max={10000000} step={25000} onChange={setIncome} format={inr} testid="tax-income" />
        <p className="text-xs text-midnight/60">Standard deduction ₹75,000 applied. Excludes surcharge for very high income. Illustrative only.</p>
      </div>
      <div className="bg-midnight rounded-2xl p-8 text-white space-y-6">
        <Result label="Estimated tax" value={inr(tax)} big />
        <div className="grid grid-cols-2 gap-4">
          <div><div className="overline text-white/50 mb-1">Take-home</div><div className="font-display text-xl text-gold">{inr(income - tax)}</div></div>
          <div><div className="overline text-white/50 mb-1">Effective rate</div><div className="font-display text-xl">{((tax/income)*100).toFixed(1)}%</div></div>
        </div>
      </div>
      <div className="lg:col-span-2"><LeadCTA source="tax" meta={{ income, tax }} /></div>
    </Shell>
  );
}
